import { useState, useCallback } from 'react';
import {
  Brain, Lightning, PuzzlePiece, Waveform, Path, GridFour, Robot, HandFist, X as XIcon,
  Textbox, Sparkle, Question, Scales, Fire, ArrowLeft
} from '@phosphor-icons/react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Leaderboard } from './Leaderboard';
import { Game2048 } from './Game2048';
import { MemoryMatch } from './MemoryMatch';
import { ReactionTest } from './ReactionTest';
import { WordScramble } from './WordScramble';
import { Hangman } from './Hangman';
import { SnakeGame } from './SnakeGame';
import { MazeEscape } from './MazeEscape';
import { ImagePuzzle } from './ImagePuzzle';
import { TicTacToe } from './TicTacToe';
import { ConnectFour } from './ConnectFour';
import { RockPaperScissors } from './RockPaperScissors';
import { AiChatGame } from './AiChatGame';
import { submitScore } from '../../lib/games';
import { toast } from 'sonner';

const GAMES = [
  { id: '2048', title: '2048', blurb: 'Slide and merge tiles to 2048', group: 'Solo', Icon: GridFour, Component: Game2048 },
  { id: 'snake', title: 'Hungry Snake', blurb: 'Eat, grow, do not bite yourself', group: 'Solo', Icon: Waveform, Component: SnakeGame },
  { id: 'maze_escape', title: 'Find The Way Out', blurb: 'Escape a fresh maze every level', group: 'Solo', Icon: Path, Component: MazeEscape },
  { id: 'image_puzzle', title: 'Image Puzzle', blurb: 'Slide the picture back together', group: 'Solo', Icon: PuzzlePiece, Component: ImagePuzzle },
  { id: 'memory_match', title: 'Memory Match', blurb: 'Find every pair in fewest moves', group: 'Solo', Icon: Brain, Component: MemoryMatch },
  { id: 'reaction_test', title: 'Reaction Test', blurb: 'How fast are your reflexes?', group: 'Solo', Icon: Lightning, Component: ReactionTest },
  { id: 'word_scramble', title: 'Word Scramble', blurb: 'Unscramble as many as you can', group: 'Solo', Icon: Textbox, Component: WordScramble },
  { id: 'hangman', title: 'Hangman', blurb: 'Guess the hidden word letter by letter', group: 'Solo', Icon: Sparkle, Component: Hangman },
  { id: 'tic_tac_toe', title: 'Tic-Tac-Toe', blurb: 'Beat an unbeatable AI (or draw)', group: 'Vs AI', Icon: XIcon, Component: TicTacToe },
  { id: 'connect_four', title: 'Connect Four', blurb: 'Outsmart the AI, connect four', group: 'Vs AI', Icon: GridFour, Component: ConnectFour },
  { id: 'rock_paper_scissors', title: 'Rock Paper Scissors', blurb: 'Build the longest win streak', group: 'Vs AI', Icon: HandFist, Component: RockPaperScissors },
  {
    id: 'twenty_questions', title: '20 Questions', blurb: 'The AI hides something. Ask away.', group: 'AI Hosted', Icon: Question,
    Component: (props) => (
      <AiChatGame
        {...props}
        gameType="twenty_questions"
        opener="I'm thinking of something. You have 20 yes/no questions. Ask me anything!"
        quickReplies={['Is it alive?', 'Is it bigger than a car?', 'I give up']}
      />
    )
  },
  {
    id: 'would_you_rather', title: 'Would You Rather', blurb: 'Impossible choices, endlessly', group: 'AI Hosted', Icon: Scales,
    Component: (props) => (
      <AiChatGame
        {...props}
        gameType="would_you_rather"
        opener="Would you rather always know when someone is lying, or always get away with lying?"
        quickReplies={['First one', 'Second one', 'Next dilemma']}
      />
    )
  },
  {
    id: 'truth_or_dare', title: 'Truth or Dare', blurb: 'Safe, solo, screen-friendly dares', group: 'AI Hosted', Icon: Fire,
    Component: (props) => (
      <AiChatGame
        {...props}
        gameType="truth_or_dare"
        opener="Truth or dare? Pick one and I'll hit you with it."
        quickReplies={['Truth', 'Dare']}
      />
    )
  }
];

const SCORED = new Set(['2048', 'snake', 'maze_escape', 'image_puzzle', 'memory_match', 'reaction_test', 'word_scramble', 'hangman', 'tic_tac_toe', 'connect_four', 'rock_paper_scissors']);

export function GamesArcade() {
  const [active, setActive] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleScore = useCallback(async (gameId, score) => {
    const result = await submitScore(gameId, score);
    if (result?.new_record) toast.success(`New personal best: ${score}!`);
    setRefreshKey((k) => k + 1);
  }, []);

  if (active) {
    const { Component, title, blurb, id } = active;
    return (
      <div className="space-y-5" data-testid="arcade-active-game">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl tracking-tight font-light">{title}</h3>
            <p className="text-xs text-muted-foreground">{blurb}</p>
          </div>
          <Button variant="outline" className="rounded-full shrink-0" onClick={() => setActive(null)} data-testid="arcade-back-button">
            <ArrowLeft size={14} className="mr-2" /> Arcade
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
          <Card className="p-4 sm:p-6">
            <div className="max-w-lg mx-auto">
              <Component onScore={(score) => handleScore(id, score)} />
            </div>
          </Card>
          {SCORED.has(id) && (
            <div className="lg:sticky lg:top-24">
              <Leaderboard game={id} refreshKey={refreshKey} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="games-arcade">
      {['Solo', 'Vs AI', 'AI Hosted'].map((group) => (
        <div key={group}>
          <div className="flex items-center gap-3 mb-4">
            {group === 'AI Hosted' && <Robot size={18} className="text-amber-300" />}
            <h3 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              {group === 'Solo' ? 'Play alone' : group === 'Vs AI' ? 'Play against the computer' : 'AI hosted word games'}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GAMES.filter((g) => g.group === group).map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => setActive(game)}
                className="text-left p-5 rounded-lg border border-white/10 bg-card/60 hover:border-amber-400/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`arcade-card-${game.id}`}
              >
                <game.Icon size={26} weight="duotone" className="text-amber-300 mb-3" />
                <p className="text-base font-medium mb-1">{game.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{game.blurb}</p>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
